// Pure: imports only the gate catalog (data, itself with no imports). Shared
// by the World (toolCallGates.js) and the Desk
// (apps/server/src/neucleos/toolGates.ts), so both decide the same steps.
import { resolveGate } from "./gateCatalog.js";
/**
 * The human steps one exact tool call must pass (session law, 23 September
 * 2026). A declaration is MAPPED when any step names the tools or actions it
 * covers: then a step applies when its tools (absent: every bound tool) include
 * this tool, its actions (absent: every call) include this call's action, and
 * its conditions (`when`, absent: none) all hold for the call's arguments.
 * An unmapped declaration with one step applies that step to every call it
 * covers. An unmapped declaration with several steps is ambiguous — which
 * step belongs to which tool or action was never written down — and answers
 * `ambiguous: true` with no steps: guessing would demand the wrong decision.
 * A call the declaration names as needing no decision (noneFor: its action,
 * its whole tool, its argument values, or a call of named tools whose
 * argument values all hold) takes no step.
 * The Desk and the World both use this function, so they never disagree.
 * `context` carries the facts only the World holds and some conditions read
 * (worldSiteHosts: the selected World's own website hostnames, from its
 * records, never from the call); a condition whose fact is absent never holds,
 * so a caller without it asks rather than waves the call through.
 *
 * An empty answer does not by itself say the call needs no decision: a mapped
 * declaration none of whose steps applies answers [] too. The doors decide
 * with stepsForCall below, which tells the two apart.
 */
export function applicableSteps(declaration, input, toolName, context) {
  if (!declaration || declaration.decisions?.kind !== "gates") return [];
  const gates = declaration.decisions.gates;
  if (namedAsNeedingNoDecision(declaration.decisions.noneFor, input, toolName, context)) return [];
  const mapped = gates.some((gate) => gate.tools !== undefined || gate.actions !== undefined || gate.when !== undefined);
  if (!mapped) return gates.length === 1 ? [...gates] : Object.assign([], { ambiguous: true });
  const action = callActionOf(input);
  return gates.filter((gate) =>
    (!gate.tools || (toolName !== undefined && gate.tools.includes(toolName))) &&
    (!gate.actions || (action !== undefined && gate.actions.includes(action))) &&
    (!gate.when || gate.when.every((condition) => conditionHolds(input, condition, context))));
}
/**
 * The explicit "no step needed" entries of a declaration (noneFor), each a
 * decision with its reason, never an omission: an action the tool packs
 * (`actions`), a whole bound tool (`tools`), argument values (`when`: every
 * condition holds for the call's arguments), or a call of named tools whose
 * argument values all hold (`calls`: [{ tools, when }]).
 */
function namedAsNeedingNoDecision(noneFor, input, toolName, context) {
  if (!noneFor) return false;
  if (Array.isArray(noneFor.actions) && noneFor.actions.includes(callActionOf(input))) return true;
  if (Array.isArray(noneFor.tools) && toolName !== undefined && noneFor.tools.includes(toolName)) return true;
  if (Array.isArray(noneFor.calls) && toolName !== undefined && noneFor.calls.some((entry) =>
    Array.isArray(entry?.tools) && entry.tools.includes(toolName) && Array.isArray(entry.when) && entry.when.length > 0
      && entry.when.every((condition) => conditionHolds(input, condition, context)))) return true;
  return Array.isArray(noneFor.when) && noneFor.when.length > 0 && noneFor.when.every((condition) => conditionHolds(input, condition, context));
}
/**
 * True when a declaration has a condition that reads the World's own website hostnames (context.worldSiteHosts);
 * given a tool, only a condition that can decide that tool's calls counts.
 */
export function declarationReadsWorldSites(declaration, toolName) {
  const noneFor = declaration?.decisions?.noneFor;
  const calls = (noneFor?.calls ?? []).filter((entry) => toolName === undefined || (Array.isArray(entry?.tools) && entry.tools.includes(toolName)));
  const conditions = [...(noneFor?.when ?? []), ...calls.flatMap((entry) => entry?.when ?? [])];
  return conditions.some((condition) => condition?.ownWorldSite === true);
}
// Hosts that are this machine or a private network by name. An IP literal of any kind is refused below.
const LOCAL_NAME = /(^|\.)(localhost|local|internal|home\.arpa)$/;
// A path, query and fragment of RFC 3986 unreserved and reserved characters, and "%" only before two hex digits.
const BROWSER_KEEPS = /^(?:[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=]|%[0-9A-Fa-f]{2})*$/;
/**
 * Whether a call's address is a page of the World's own website: https on the default port, no credentials, a host
 * name (never an IP literal, never localhost, *.local or *.localhost) that is exactly one of `hosts` (the World's own
 * website hostnames, from its records). Anything else, or no hosts, is not.
 *
 * The address must also be written exactly as the browser will load it (PR #1717): its own WHATWG href, or a bare
 * origin whose href only adds the root "/" ("https://shop.example.com"), beginning "https://", with no backslash,
 * whitespace or control character. A spelling a parser repairs ("https:/host", "https:\\host", "https:///host",
 * "HTTPS://", ":443", a tab or a leading space) may be repaired differently by the browser path
 * (@cleo/shared/preview normalizePreviewUrl reads "https:/shop.example.com/x" as host "https"), so the host judged
 * here would not be the host loaded. Such a call asks.
 */
export function ownWorldSiteUrl(value, hosts) {
  if (typeof value !== "string" || !Array.isArray(hosts) || hosts.length === 0) return false;
  if (!value.startsWith("https://") || /[\\\s\u0000-\u001f\u007f-\u009f]/.test(value)) return false;
  let url;
  try { url = new URL(value); } catch { return false; }
  if (url.href !== value && !(value === url.origin && url.href === `${url.origin}/`)) return false;
  // Everything after the host holds only characters every browser leaves as written (RFC 3986 unreserved and reserved,
  // and "%" with two hex digits): Electron's GURL escapes others ("/a|b" loads as "/a%7Cb"), which Node's href keeps.
  if (!BROWSER_KEEPS.test(value.slice(url.origin.length))) return false;
  if (url.protocol !== "https:" || url.port !== "" || url.username !== "" || url.password !== "") return false;
  const host = url.hostname.toLowerCase().replace(/\.$/, "");
  if (host === "" || host.startsWith("[") || host.includes(":") || /^[0-9.]+$/.test(host) || LOCAL_NAME.test(host)) return false;
  return hosts.some((entry) => typeof entry === "string" && entry.toLowerCase().replace(/\.$/, "") === host);
}
/**
 * What one exact call must pass, and why (4 October 2026, known hole 6 of
 * docs/approval-gates-design.md). Both World doors (toolCallGates.js,
 * mcpCanvas.js) and the Desk's gate bridge decide with this:
 *
 *   need "none":  no step. The declaration says no decision is needed for
 *                 this call: kind "none", or a noneFor entry names it.
 *   need "steps": the declared steps that apply to this exact call.
 *   need "ask":   a step declaration that names no step for this call and
 *                 does not say it needs none (or an ambiguous one: several
 *                 unmapped steps). Before, such a call ran with no approval
 *                 and no record (pausing a published offer). Now it asks the
 *                 person under the declaration's strictest step they can
 *                 answer (stepToAsk), opened HUMAN_IN_THE_LOOP and decided by
 *                 the person alone (no delegation, and their request is not
 *                 the decision): it is never refused and never runs unasked.
 *                 `steps` holds that one step.
 *
 * A declaration none of whose steps the person answers with an approval (a
 * World's own declaration whose only step is a receipt, HO-ACT-RECEIPT-01)
 * asks for nothing stricter than its own steps: such a call takes the
 * strictest of them under that step's own settings (need "steps"), so it is
 * recorded as that step records, and never waits on a question the Inbox and
 * the card cannot answer (4 October 2026).
 */
export function stepsForCall(declaration, input, toolName, context) {
  if (!declaration || declaration.decisions?.kind !== "gates") return { need: "none", steps: [] };
  const steps = applicableSteps(declaration, input, toolName, context);
  if (steps.length > 0) return { need: "steps", steps: [...steps] };
  if (!steps.ambiguous && namedAsNeedingNoDecision(declaration.decisions.noneFor, input, toolName, context)) return { need: "none", steps: [] };
  const gates = declaration.decisions.gates;
  const ask = stepToAsk(gates, toolName);
  if (ask) return { need: "ask", steps: [ask] };
  const strictest = strictestStep(gates);
  return strictest ? { need: "steps", steps: [strictest] } : { need: "none", steps: [] };
}
// The person's own decisions (C) and the World owner's (O). An attorney's, two
// people's or nobody's (a receipt) is never asked of the person alone.
const PERSON_DECIDES = ["C", "O"];
/**
 * Whether the person answers a step of this gate class on its card: they (or
 * the World's owner) decide it, and "approved", what the card and the Inbox
 * record, satisfies it. A receipt class (HO-ACT-RECEIPT-01: observed or
 * reverted) is not a question: opened as one, the Inbox shows nothing to
 * decide and the card's approval is refused (GATE_OUTCOME_NOT_ALLOWED).
 */
export function personAnswersGate(gateKey) {
  const entry = resolveGate(gateKey);
  return !!entry && PERSON_DECIDES.includes(entry.deciderClass)
    && entry.allowedOutcomes.includes("approved") && entry.satisfyingOutcomes.includes("approved");
}
/**
 * The step a call no step names is asked under: the strictest step the person
 * answers (personAnswersGate), its own tool's first (a step whose tools are
 * absent or name this tool), then any of the declaration's. Null when the
 * person answers none of its steps. The question is HUMAN_IN_THE_LOOP and the
 * person's alone whichever step it is asked under, so preferring the call's
 * own tool changes only what the card describes, never how strict it is.
 */
export function stepToAsk(gates, toolName) {
  const answerable = (Array.isArray(gates) ? gates : []).filter((gate) => gate && typeof gate.gateKey === "string" && personAnswersGate(gate.gateKey));
  const own = answerable.filter((gate) => !Array.isArray(gate.tools) || (toolName !== undefined && gate.tools.includes(toolName)));
  return strictestStep(own.length > 0 ? own : answerable);
}
const KIND_STRICTNESS = { HUMAN_IN_THE_LOOP: 3, HUMAN_INPUT: 2, HUMAN_ON_THE_LOOP: 1, HUMAN_OBSERVATION: 0 };
/**
 * The strictest of a declaration's own steps: a step the person decides every
 * time (humanDelegation "not-delegable", Cleo Can 10) first, then the
 * strictest default kind, then the first declared. No step is invented: the
 * person is asked under one the declaration already has.
 */
export function strictestStep(gates) {
  let best = null;
  let bestRank = -Infinity;
  for (const gate of Array.isArray(gates) ? gates : []) {
    if (!gate || typeof gate.gateKey !== "string") continue;
    const rank = (gate.humanDelegation === "not-delegable" ? 10 : 0) + (KIND_STRICTNESS[gate.defaultKind] ?? -1);
    if (rank > bestRank) { best = gate; bestRank = rank; }
  }
  return best;
}
/**
 * One argument condition: `equals` one value, `oneOf` several, or `noneOf`
 * several. An absent argument matches `noneOf`, so a step keyed to a
 * non-default value (status paused or withdrawn) leaves the default call
 * (status omitted, meaning active) to the other step.
 */
function conditionHolds(input, condition, context) {
  const value = callArgument(input, condition.field);
  // `absent`: the argument is not given. `absentOr`: not given, or exactly this value. `ownWorldSite`: an address of
  // the World's own website (ownWorldSiteUrl), read against the World's hostnames in `context`, never the call's.
  if (condition.absent === true) return value === undefined;
  if (Object.prototype.hasOwnProperty.call(condition, "absentOr")) return value === undefined || value === condition.absentOr;
  if (condition.ownWorldSite === true) return ownWorldSiteUrl(value, context?.worldSiteHosts);
  if (Array.isArray(condition.oneOf)) return condition.oneOf.includes(value);
  if (Array.isArray(condition.noneOf)) return !condition.noneOf.includes(value);
  return value === condition.equals;
}
/** One argument of the call: its top level, or its request object's. */
function callArgument(args, field) {
  if (!args || typeof args !== "object" || Array.isArray(args)) return undefined;
  if (Object.prototype.hasOwnProperty.call(args, field)) return args[field];
  const nested = args.request;
  return nested && typeof nested === "object" && !Array.isArray(nested) ? nested[field] : undefined;
}
/** The call's action: `action`, or `request.action` for tools that nest it. */
export function callActionOf(args) {
  if (!args || typeof args !== "object" || Array.isArray(args)) return undefined;
  if (typeof args.action === "string") return args.action;
  const nested = args.request;
  return nested && typeof nested === "object" && !Array.isArray(nested) && typeof nested.action === "string" ? nested.action : undefined;
}
